import { FolderOpen, HelpCircle, Clipboard, Map as MapIcon, Settings, Workflow } from 'lucide-react'
import React, { memo, useState, useEffect, useMemo } from 'react'
import { MdPalette } from 'react-icons/md' // Add this import
import { MdLightMode, MdDarkMode } from 'react-icons/md'
import { toast } from 'sonner'
import { useTheme } from '@/components/UIUX/theme-provider'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { createDefaultHookResult } from '@/hooks/hookTypes'
import { useLogout } from '@/network/axiosInstance'
import { useUpdateProjectDetails } from '@/stores/projectsStore'

// @ts-ignore
import '~/styles/OverlayTopBar.css'

// Define props interface
interface OverlayTopBarProps {
  readonly projectId?: string
  readonly projectName?: string
  readonly projectDescription?: string
  readonly showStartContent?: boolean
  readonly showLogo?: boolean
  readonly showDashboardButton?: boolean
  readonly userName: string
  theme?: 'light' | 'dark'
  toggleTheme?: () => void
  backgroundPreset?: string // Add this
  backgroundPresets?: {
    id: string
    name: string
    background: string
    color: string
  }[] // Add this
  setBackgroundPreset?: (preset: string) => void // Add this
  onShowFileDialog?: () => void
  onShowHelpGuide?: () => void
  onShowNodeIndex?: () => void
  hasCopiedNode?: boolean
  onPasteNode?: () => void
  // Additive: Creativeness slider props
  creativeness?: number // 0-100
  setCreativeness?: (value: number) => void
  onShowProjectSettings?: () => void // Add this prop
  onShowBlueprints?: () => void // Add Blueprint handler
}

function OverlayTopBar({
  projectId = undefined,
  projectName = undefined,
  projectDescription = 'No description provided.',
  showStartContent = true,
  showLogo = true,
  showDashboardButton = true,
  userName,
  theme,
  toggleTheme,
  backgroundPreset,
  backgroundPresets,
  setBackgroundPreset,
  onShowFileDialog,
  onShowHelpGuide,
  onShowNodeIndex,
  hasCopiedNode,
  onPasteNode,
  // Additive: Creativeness slider props
  creativeness,
  setCreativeness,
  onShowProjectSettings, // Add this prop
  onShowBlueprints, // Add Blueprint handler
}: OverlayTopBarProps) {
  // Hooks
  const getLogout = (useLogout() || createDefaultHookResult())
  const [name, setName] = useState(userName)
  const [visible, setVisible] = useState(false)
  const [theme2, setTheme2] = useState<'light' | 'dark'>('light')
  const [isOpen, setIsOpen] = useState(false)
  const [bgMenuOpen, setBgMenuOpen] = useState(false)
  const { setTheme } = useTheme()

  // Effects
  useEffect(() => {
    setName(userName)
  }, [userName])

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)'
    ).matches
    const initialTheme = savedTheme || (prefersDark ? 'dark' : 'light')
    setTheme2(initialTheme)
    document.documentElement.setAttribute('data-theme', initialTheme)
    document.documentElement.style.colorScheme = initialTheme
  }, [])

  // Event handlers
  const handleSignOut = (event: React.MouseEvent) => {
    event.preventDefault()
    getLogout.execute()
  }

  const gotoDashboard = (event: React.MouseEvent) => {
    event.preventDefault()
    window.location.href = '/dashboard?noAnim=true'
  }

  const toggleUserInfo = () => {
    setVisible((prevVisible) => !prevVisible)
  }

  const toggleTheme2 = () => {
    typeof toggleTheme === 'function' && toggleTheme()
    const newTheme = theme2 === 'light' ? 'dark' : 'light'
    setTheme2(newTheme)
    if (newTheme === 'light') setTheme('light')
    else setTheme('dark')

    // Update DOM
    document.documentElement.setAttribute('data-theme', newTheme)
    document.documentElement.style.colorScheme = newTheme

    // Save to local storage
    localStorage.setItem('theme', newTheme)
  }

  // Add state for editing
  const [isEditing, setIsEditing] = useState(false)
  const [editableProjectName, setEditableProjectName] = useState(projectName)

  const updateProjectDetails = useUpdateProjectDetails()

  // Handle save
  const saveProjectName = () => {
    updateProjectDetails.execute({
      id: projectId,
      data: { projectName: editableProjectName },
      onSuccess: () => {
        setIsEditing(false)
        toast.success('Project name updated successfully')
      },
      onError: () => {
        toast.error('Error updating project name')
      },
    })
  }

  // Creativeness slider handler (additive)
  const handleCreativenessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (setCreativeness) {
      setCreativeness(Number(e.target.value))
    }
  }

  // Render functions
  const renderStartContent = () => {
    if (!showStartContent) return null
    return (
      <div style={{ display: 'flex', alignItems: 'center', position: 'absolute', left: '10px', top: '10px', pointerEvents: 'auto' }}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="spechops-logo-container cursor-pointer" onClick={gotoDashboard}>
                <img src="/spechopslogo2.svg" alt="Spechops Logo" className="spechops-logo" />
              </div>
            </TooltipTrigger>
            <TooltipContent>Spechops Dashboard</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {/* Project name glitch text */}
        <div className="ml-0 mb-1 relative">
          <p className="glitch-text" data-text={projectName}>{projectName}</p>
          <style jsx={'true'}>{`
            .glitch-text {
              position: relative;
              color: rgba(0, 0, 0, 0.9);
              font-weight: bold;
              text-shadow:
                0.5px 0.5px 0 rgba(255, 0, 0, 0.3),
                -0.5px -0.5px 0 rgba(0, 255, 0, 0.3),
                0.5px -0.5px 0 rgba(0, 0, 255, 0.3);
              animation: glitch 32s infinite;
            }

            .glitch-text::before,
            .glitch-text::after {
              content: attr(data-text);
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              opacity: 0.8;
            }

            .glitch-text::before {
              left: 0.5px;
              text-shadow: 0.5px 0 rgba(255, 0, 193, 0.5);
              clip: rect(0, 900px, 0, 0);
              animation: glitch-anim 8s infinite linear alternate-reverse;
            }

            .glitch-text::after {
              left: -0.5px;
              text-shadow: -0.5px 0 rgba(0, 255, 249, 0.5);
              animation: glitch-anim2 12s infinite linear alternate-reverse;
            }

            @keyframes glitch {
              0%, 100% { text-shadow: 0.5px 0.5px 0 rgba(255,0,0,0.3), -0.5px -0.5px 0 rgba(0,255,0,0.3), 0.5px -0.5px 0 rgba(0,0,255,0.3); }
              25% { text-shadow: -0.5px 0.5px 0 rgba(255,0,0,0.3), 0.5px -0.5px 0 rgba(0,255,0,0.3), -0.5px 0.5px 0 rgba(0,0,255,0.3); }
              50% { text-shadow: 0.5px -0.5px 0 rgba(255,0,0,0.3), -0.5px 0.5px 0 rgba(0,255,0,0.3), 0.5px 0.5px 0 rgba(0,0,255,0.3); }
              75% { text-shadow: -0.5px -0.5px 0 rgba(255,0,0,0.3), 0.5px 0.5px 0 rgba(0,255,0,0.3), -0.5px -0.5px 0 rgba(0,0,255,0.3); }
            }

            @keyframes glitch-anim {
              0% { clip: rect(9px, 9999px, 19px, 0); }
              20% { clip: rect(21px, 9999px, 6px, 0); }
              40% { clip: rect(3px, 9999px, 15px, 0); }
              60% { clip: rect(12px, 9999px, 9px, 0); }
              80% { clip: rect(18px, 9999px, 14px, 0); }
              100% { clip: rect(7px, 9999px, 16px, 0); }
            }

            @keyframes glitch-anim2 {
              0% { clip: rect(15px, 9999px, 11px, 0); }
              20% { clip: rect(8px, 9999px, 19px, 0); }
              40% { clip: rect(14px, 9999px, 5px, 0); }
              60% { clip: rect(4px, 9999px, 17px, 0); }
              80% { clip: rect(18px, 9999px, 9px, 0); }
              100% { clip: rect(12px, 9999px, 15px, 0); }
            }

            .glitch-text {
              filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.5));
            }
          `}</style>
        </div>
      </div>
    )
  }

  const renderEndContent = useMemo(() => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          position: 'absolute',
          top: '5px',
          right: '-10px',
          pointerEvents: 'auto',
        }}
      >
        <div className="topbar-glass-buttons">
          {/* Project Files, Help, Map, Paste */}
          {backgroundPresets && backgroundPresets.length > 0 && (<>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="topbar-glass-btn" onClick={onShowFileDialog}>
                    <FolderOpen className="h-5 w-5" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Project files</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="topbar-glass-btn" onClick={onShowHelpGuide}>
                    <HelpCircle className="h-5 w-5" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Help & Guide</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="topbar-glass-btn" onClick={onShowNodeIndex}>
                    <MapIcon className="h-5 w-5" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Show Node Map</TooltipContent>
              </Tooltip>
            </TooltipProvider></>       )}
          {hasCopiedNode && onPasteNode && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="topbar-glass-btn" onClick={onPasteNode}>
                    <Clipboard className="h-5 w-5" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Paste Node (Ctrl+V)</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {/* Separator */}
          {backgroundPresets && backgroundPresets.length > 0 && (<>
            <div className="topbar-glass-separator" />
          </>)}
          {/* Settings/Gear Button */}
          {typeof onShowProjectSettings === 'function' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="topbar-glass-btn" onClick={onShowProjectSettings} tabIndex={0} role="button" aria-label="Project Settings">
                    <Settings className="h-5 w-5" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Project Settings</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {/* Blueprint/Workflow Button */}
          {typeof onShowBlueprints === 'function' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="topbar-glass-btn" onClick={onShowBlueprints} tabIndex={0} role="button" aria-label="Blueprints & Workflows">
                    <Workflow className="h-5 w-5" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Blueprints & Workflows</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {/* Creativeness Slider (additive, only if props provided) */}
          {typeof creativeness === 'number' && typeof setCreativeness === 'function' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="topbar-glass-btn topbar-creativeness-slider">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={creativeness}
                      onChange={handleCreativenessChange}
                      className=""
                      aria-label="Creativeness Slider"
                    />
                    <span className="topbar-creativeness-slider-value">{creativeness}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Project Creativeness Level</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Background Selector Dropdown with tooltip */}
          {backgroundPresets && backgroundPresets.length > 0 && (
  <DropdownMenu open={bgMenuOpen} onOpenChange={setBgMenuOpen}>
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="topbar-glass-btn mr-0 border border-blue-300 bg-transparent hover:bg-sky-500/10 transition-colors duration-200 p-2 rounded-full cursor-pointer">
            <DropdownMenuTrigger asChild>
              <span>
                <MdPalette size={20} />
              </span>
            </DropdownMenuTrigger>
          </div>
        </TooltipTrigger>
        <TooltipContent>Background Styles</TooltipContent>
      </Tooltip>
    </TooltipProvider>
    <DropdownMenuContent
      align="end"
      sideOffset={5}
      className="w-56 max-h-[70vh] overflow-y-auto"
    >
                <DropdownMenuLabel>Background Style</DropdownMenuLabel>
                <DropdownMenuSeparator />

                {/* Standard Themes Group */}
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Standard Themes
                </DropdownMenuLabel>
                {backgroundPresets.slice(0, 10).map((preset) => (
                  <DropdownMenuItem
                    key={preset.id}
                    onSelect={() => {
                      setBackgroundPreset(preset.id)
                      setBgMenuOpen(false)
                    }}
                    className="flex items-center justify-between"
                  >
                    <span>{preset.name}</span>
                    <div
                      className="w-8 h-6 rounded border border-gray-300 flex-shrink-0"
                      style={{
                        background: preset.background,
                        boxShadow:
                          backgroundPreset === preset.id
                            ? '0 0 0 2px rgba(59, 130, 246, 0.5)'
                            : 'none',
                      }}
                    />
                  </DropdownMenuItem>
                ))}

                {/* Specialized Themes Group */}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Specialized Themes
                </DropdownMenuLabel>
                {backgroundPresets.slice(10, 20).map((preset) => (
                  <DropdownMenuItem
                    key={preset.id}
                    onSelect={() => {
                      setBackgroundPreset(preset.id)
                      setBgMenuOpen(false)
                    }}
                    className="flex items-center justify-between"
                  >
                    <span>{preset.name}</span>
                    <div
                      className="w-8 h-6 rounded border border-gray-300 flex-shrink-0"
                      style={{
                        background: preset.background,
                        boxShadow:
                          backgroundPreset === preset.id
                            ? '0 0 0 2px rgba(59, 130, 246, 0.5)'
                            : 'none',
                      }}
                    />
                  </DropdownMenuItem>
                ))}

                {/* Animated Themes Group */}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground" >
                  Animated Themes
                </DropdownMenuLabel>
                {backgroundPresets.slice(20).map((preset) => (
                  <DropdownMenuItem
                    key={preset.id}
                    onSelect={() => {
                      setBackgroundPreset(preset.id)
                      setBgMenuOpen(false)
                    }}
                    className="flex items-center justify-between"
                  >
                    <span>{preset.name}</span>
                    <div
                      className={`w-8 h-6 rounded border border-gray-300 flex-shrink-0 ${
                        preset.animated ? 'relative overflow-hidden' : ''
                      }`}
                      style={{
                        background: preset.background,
                        boxShadow:
                          backgroundPreset === preset.id
                            ? '0 0 0 2px rgba(59, 130, 246, 0.5)'
                            : 'none',
                      }}
                    >
                      {preset.animated && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs text-white">✦</span>
                        </div>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Theme toggle button with tooltip */}
          <TooltipProvider>
            <Tooltip >
              <TooltipTrigger asChild>
                <div
                  onClick={toggleTheme2}
                  className="topbar-glass-btn mr-0 border border-blue-300 bg-transparent hover:bg-sky-500/10 transition-colors duration-200 p-2 rounded-full cursor-pointer"
                >
                  {theme === 'light' ? (
                    <MdDarkMode size={20} />
                  ) : (
                    <MdLightMode size={20} />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {theme === 'light'
                  ? 'Switch to Dark Mode'
                  : 'Switch to Light Mode'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {/* Profile Dropdown with tooltip */}
          <TooltipProvider>
  <Tooltip>
    <TooltipTrigger asChild>
      <div className="topbar-glass-btn cursor-pointer border border-blue-300 bg-transparent hover:bg-sky500/10 transition-colors duration-200">
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger onClick={(e) => {

            }}>
            <Avatar className="avatar" style={{ marginLeft: '10px', borderWidth: '2px', borderColor: '#000' }}>
              <AvatarImage src={undefined} alt={userName || 'User'} />
              <AvatarFallback>{userName?.charAt(0) || 'U'}</AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>{userName || 'User Menu'}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={gotoDashboard}>
              Dashboard
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={handleSignOut}>Logout</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TooltipTrigger>
    <TooltipContent>User Menu</TooltipContent>
  </Tooltip>
</TooltipProvider>
        </div>
      </div>
    )
  }, [visible, name, theme, handleSignOut, toggleTheme, onShowFileDialog, onShowHelpGuide, onShowNodeIndex, hasCopiedNode, onPasteNode, bgMenuOpen, setBgMenuOpen, backgroundPresets, backgroundPreset, setBackgroundPreset, isOpen, setIsOpen, userName])

  return (
    <div
      className="overlay-top-bar"
      style={{
        pointerEvents: 'none',
        position: 'fixed',
        top: 0,
        width: '100%',
        zIndex: 100,
      }}
    >
      {renderStartContent()}
      {renderEndContent}
    </div>
  )
}

export default memo(OverlayTopBar)
